import {
  Autocomplete,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Close } from '@mui/icons-material';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Section } from '../../../../components/ui/Section';
import { MarketItem, client } from '../../../../app/services/api/orderManagementClient';
import useLogError from '../../../../hooks/useLogError';

export interface SubstituteItemEntry {
  item: MarketItem;
  priority: number;
}

type Props = {
  companyId: number;
  customerTypeId?: number;
  contentId?: number;
  substitutes: SubstituteItemEntry[];
  onChange: (substitutes: SubstituteItemEntry[]) => void;
};

/**
 * Demo Mode only - lets a chef/procurement user record other same-market products that can
 * stand in for this one when a supplier can't fulfil it. Ordered by priority (first tried
 * first). No backend persistence exists yet - this is a locally-held relation only.
 */
const SubstituteItems: FC<Props> = ({ companyId, customerTypeId, contentId, substitutes, onChange }) => {
  const { t } = useTranslation('productManagement');
  const { logError } = useLogError();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MarketItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const latestRequestId = useRef(0);

  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2 || !companyId || customerTypeId === undefined) {
      setSearchResults([]);
      return;
    }

    const requestId = ++latestRequestId.current;
    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await client.getMarketItems({
          companyId: companyId.toString(),
          customerTypeId: customerTypeId.toString(),
          searchTerm: term,
          pageNumber: 0,
          pageSize: 20,
        });
        if (requestId !== latestRequestId.current) return;
        const existingIds = new Set(substitutes.map((s) => s.item.contentId));
        const results = response?.success && response.data
          ? (response.data.items || []).filter(
            (item) => item.contentId !== contentId && !existingIds.has(item.contentId),
          )
          : [];
        setSearchResults(results);
      } catch {
        if (requestId === latestRequestId.current) logError('Failed to search for substitute items');
      } finally {
        if (requestId === latestRequestId.current) setIsSearching(false);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, companyId, customerTypeId, contentId, substitutes]);

  const handleAdd = (item: MarketItem | null) => {
    if (!item) return;
    onChange([...substitutes, { item, priority: substitutes.length + 1 }]
      .map((entry, index) => ({ ...entry, priority: index + 1 })));
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemove = (contentIdToRemove: number | undefined) => {
    onChange(
      substitutes
        .filter((s) => s.item.contentId !== contentIdToRemove)
        .map((entry, index) => ({ ...entry, priority: index + 1 })),
    );
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= substitutes.length) return;
    const reordered = [...substitutes];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onChange(reordered.map((entry, i) => ({ ...entry, priority: i + 1 })));
  };

  const handleViewItem = (item: MarketItem) => {
    const params = new URLSearchParams();
    params.set('companyId', item.companyId.toString());
    if (item.customerTypeId !== undefined) {
      params.set('customerTypeId', item.customerTypeId.toString());
    }
    navigate(`/procurement/products/edit/${item.contentId}?${params.toString()}`);
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Section>
        <Typography
          variant="textMD"
          fontWeight={600}
          component="div"
          sx={{ p: 4 }}
        >
          {t('substituteItems', 'Substitute Items')}
        </Typography>
        <Box sx={{ px: 4, pb: 4 }}>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 3 }}>
            {t(
              'substituteItemsIntro',
              'If a supplier can\'t fulfil this product, these items can be used as a substitute, in priority order.',
            )}
          </Typography>
          <Autocomplete
            options={searchResults}
            loading={isSearching}
            getOptionLabel={(option) => `${option.itemNumber} - ${option.itemName}`}
            filterOptions={(options) => options}
            inputValue={searchTerm}
            onInputChange={(_, value) => setSearchTerm(value)}
            value={null}
            onChange={(_, value) => handleAdd(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('addSubstituteItem', 'Add a substitute item')}
                placeholder={t('substituteSearchPlaceholder', 'Search by item number or name')}
              />
            )}
            sx={{ maxWidth: 480 }}
          />
        </Box>
        {substitutes.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('priority', 'Priority')}</TableCell>
                  <TableCell>{t('itemNumber')}</TableCell>
                  <TableCell>{t('itemName')}</TableCell>
                  <TableCell align="right">{t('actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {substitutes.map((entry, index) => (
                  <TableRow key={entry.item.contentId}>
                    <TableCell>{entry.priority}</TableCell>
                    <TableCell>
                      <Typography
                        component="a"
                        variant="textSM"
                        onClick={() => handleViewItem(entry.item)}
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {entry.item.itemNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{entry.item.itemName}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small"
                        disabled={index === 0}
                        onClick={() => handleMove(index, -1)}>
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                      <IconButton size="small"
                        disabled={index === substitutes.length - 1}
                        onClick={() => handleMove(index, 1)}>
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                      <IconButton size="small"
                        onClick={() => handleRemove(entry.item.contentId)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>
    </Box>
  );
};

export default SubstituteItems;
